import {
  Close as CloseIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment, { Moment } from "moment-timezone";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import {
  COMMON_TIMEZONES,
  DAYS_OF_WEEK,
  SCHEDULED_TASK_FREQUENCY_DAILY,
  SCHEDULED_TASK_FREQUENCY_MONTHLY,
  SCHEDULED_TASK_FREQUENCY_WEEKLY,
  SCHEDULED_TASK_TYPE_ONE_TIME,
  SCHEDULED_TASK_TYPE_RECURRING,
} from "../../../constants";
import { Employee } from "../../../types";
import ChatSettingsDialog from "../ChatSettingsDialog";

interface ScheduledTaskFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (task: ScheduledTaskFormData) => Promise<void>;
  editingTask?: ScheduledTask | null;
  employees: Array<Employee>;
  company: { id: string; name: string };
  getToken: () => Promise<string>;
  currentPlan: any;
}

interface ScheduledTask {
  id?: string;
  name: string;
  employeeId: string;
  prompt: string;
  scheduleType: string;
  scheduledAt?: string | null;
  recurringConfig?: {
    frequency: string;
    time: string;
    daysOfWeek?: number[];
    dayOfMonth?: number;
  } | null;
  contextSettings?: any;
  timezone: string;
  status?: string;
  employee?: {
    id: string;
    name: string;
    picture?: string;
    title?: string;
  };
}

interface ScheduledTaskFormData {
  name: string;
  employeeId: string;
  prompt: string;
  scheduleType: string;
  scheduledAt?: string | null;
  recurringConfig?: {
    frequency: string;
    time: string;
    daysOfWeek?: number[];
    dayOfMonth?: number;
  } | null;
  contextSettings?: any;
  timezone: string;
}

export default function ScheduledTaskFormDialog({
  open,
  onClose,
  onSave,
  editingTask,
  employees,
  company,
  getToken,
  currentPlan,
}: ScheduledTaskFormDialogProps) {
  const [name, setName] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [scheduleType, setScheduleType] = useState(SCHEDULED_TASK_TYPE_ONE_TIME);
  const [scheduledDate, setScheduledDate] = useState<Moment | null>(moment().add(1, "hour"));
  const [scheduledTime, setScheduledTime] = useState<Moment | null>(moment().add(1, "hour"));
  const [frequency, setFrequency] = useState(SCHEDULED_TASK_FREQUENCY_DAILY);
  const [recurringTime, setRecurringTime] = useState<Moment | null>(moment().hour(9).minute(0));
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([1]); // Monday by default
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
  );
  const [contextSettings, setContextSettings] = useState<any>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (editingTask) {
      setName(editingTask.name || "");
      setEmployeeId(editingTask.employeeId || "");
      setPrompt(editingTask.prompt || "");
      setScheduleType(editingTask.scheduleType || SCHEDULED_TASK_TYPE_ONE_TIME);
      setTimezone(editingTask.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      setContextSettings(editingTask.contextSettings || null);

      if (editingTask.scheduledAt) {
        const scheduled = moment(editingTask.scheduledAt);
        setScheduledDate(scheduled);
        setScheduledTime(scheduled);
      }

      if (editingTask.recurringConfig) {
        setFrequency(editingTask.recurringConfig.frequency || SCHEDULED_TASK_FREQUENCY_DAILY);
        if (editingTask.recurringConfig.time) {
          const [hours, minutes] = editingTask.recurringConfig.time.split(":");
          setRecurringTime(moment().hour(parseInt(hours)).minute(parseInt(minutes)));
        }
        if (editingTask.recurringConfig.daysOfWeek) {
          setDaysOfWeek(editingTask.recurringConfig.daysOfWeek);
        }
        if (editingTask.recurringConfig.dayOfMonth) {
          setDayOfMonth(editingTask.recurringConfig.dayOfMonth);
        }
      }
    } else {
      // Reset form for new task
      setName("");
      setEmployeeId("");
      setPrompt("");
      setScheduleType(SCHEDULED_TASK_TYPE_ONE_TIME);
      setScheduledDate(moment().add(1, "hour"));
      setScheduledTime(moment().add(1, "hour"));
      setFrequency(SCHEDULED_TASK_FREQUENCY_DAILY);
      setRecurringTime(moment().hour(9).minute(0));
      setDaysOfWeek([1]);
      setDayOfMonth(1);
      setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York");
      setContextSettings(null);
    }
  }, [editingTask, open]);

  const handleSave = async () => {
    if (!name.trim() || !employeeId || !prompt.trim()) {
      return;
    }

    setSaving(true);
    try {
      const taskData: ScheduledTaskFormData = {
        name: name.trim(),
        employeeId,
        prompt: prompt.trim(),
        scheduleType,
        timezone,
        contextSettings: contextSettings || undefined,
      };

      if (scheduleType === SCHEDULED_TASK_TYPE_ONE_TIME) {
        // Combine date and time, convert to UTC ISO string
        if (scheduledDate && scheduledTime) {
          const combined = moment.tz(
            {
              year: scheduledDate.year(),
              month: scheduledDate.month(),
              date: scheduledDate.date(),
              hour: scheduledTime.hour(),
              minute: scheduledTime.minute(),
            },
            timezone
          );
          taskData.scheduledAt = combined.utc().toISOString();
        }
      } else {
        // Recurring config
        taskData.recurringConfig = {
          frequency,
          time: recurringTime ? recurringTime.format("HH:mm") : "09:00",
        };

        if (frequency === SCHEDULED_TASK_FREQUENCY_WEEKLY) {
          taskData.recurringConfig.daysOfWeek = daysOfWeek;
        } else if (frequency === SCHEDULED_TASK_FREQUENCY_MONTHLY) {
          taskData.recurringConfig.dayOfMonth = dayOfMonth;
        }
      }

      await onSave(taskData);
      onClose();
    } catch (error) {
      console.error("Error saving scheduled task:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDayOfWeekToggle = (day: number) => {
    setDaysOfWeek((prev) => {
      if (prev.includes(day)) {
        // Don't allow removing the last day
        if (prev.length === 1) return prev;
        return prev.filter((d) => d !== day);
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  const getScheduleSummary = () => {
    if (scheduleType === SCHEDULED_TASK_TYPE_ONE_TIME) {
      if (scheduledDate && scheduledTime) {
        const combined = moment.tz(
          {
            year: scheduledDate.year(),
            month: scheduledDate.month(),
            date: scheduledDate.date(),
            hour: scheduledTime.hour(),
            minute: scheduledTime.minute(),
          },
          timezone
        );
        return `Once on ${combined.format("MMMM D, YYYY")} at ${combined.format("h:mm A")}`;
      }
      return "Select a date and time";
    }

    const timeStr = recurringTime ? recurringTime.format("h:mm A") : "9:00 AM";

    if (frequency === SCHEDULED_TASK_FREQUENCY_DAILY) {
      return `Every day at ${timeStr}`;
    } else if (frequency === SCHEDULED_TASK_FREQUENCY_WEEKLY) {
      const dayNames = daysOfWeek
        .map((d) => DAYS_OF_WEEK.find((day) => day.value === d)?.label)
        .filter(Boolean)
        .join(", ");
      return `Every ${dayNames} at ${timeStr}`;
    } else if (frequency === SCHEDULED_TASK_FREQUENCY_MONTHLY) {
      const suffix =
        dayOfMonth === 1 || dayOfMonth === 21 || dayOfMonth === 31
          ? "st"
          : dayOfMonth === 2 || dayOfMonth === 22
          ? "nd"
          : dayOfMonth === 3 || dayOfMonth === 23
          ? "rd"
          : "th";
      return `On the ${dayOfMonth}${suffix} of every month at ${timeStr}`;
    }

    return "";
  };

  const selectedEmployee = employees.find((e) => e.id === employeeId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">
            {editingTask ? "Edit Scheduled Task" : "New Scheduled Task"}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            {/* Task Name */}
            <TextField
              label="Task Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Daily Market Report"
            />

            {/* Employee Selection */}
            <FormControl fullWidth required>
              <InputLabel>AI Employee</InputLabel>
              <Select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                label="AI Employee"
                renderValue={() => {
                  if (!selectedEmployee) return "";
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar src={selectedEmployee.picture} sx={{ width: 24, height: 24 }}>
                        {selectedEmployee.name?.[0]}
                      </Avatar>
                      <Typography>{selectedEmployee.name}</Typography>
                    </Box>
                  );
                }}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    <ListItemAvatar>
                      <Avatar src={employee.picture} sx={{ width: 32, height: 32 }}>
                        {employee.name?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={employee.name} secondary={employee.title} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Settings Button */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title="Configure which tools and capabilities are available when this task runs">
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={() => setSettingsDialogOpen(true)}
                  size="small"
                >
                  Task Settings
                </Button>
              </Tooltip>
              {contextSettings && (
                <Typography variant="caption" color="text.secondary">
                  Custom settings configured
                </Typography>
              )}
            </Box>

            {/* Prompt */}
            <TextField
              label="Prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              fullWidth
              required
              multiline
              minRows={3}
              maxRows={8}
              placeholder="What would you like the AI employee to do?"
              helperText="This message will be sent to the AI employee at the scheduled time"
            />

            {/* Schedule Type Toggle */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Schedule Type
              </Typography>
              <ToggleButtonGroup
                value={scheduleType}
                exclusive
                onChange={(_, value) => value && setScheduleType(value)}
                fullWidth
              >
                <ToggleButton value={SCHEDULED_TASK_TYPE_ONE_TIME}>One-time</ToggleButton>
                <ToggleButton value={SCHEDULED_TASK_TYPE_RECURRING}>Recurring</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* One-time Schedule */}
            {scheduleType === SCHEDULED_TASK_TYPE_ONE_TIME && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <DatePicker
                  label="Date"
                  value={scheduledDate}
                  onChange={(date) => setScheduledDate(date as Moment | null)}
                  minDate={moment()}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
                <TimePicker
                  label="Time"
                  value={scheduledTime}
                  onChange={(time) => setScheduledTime(time as Moment | null)}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </Box>
            )}

            {/* Recurring Schedule */}
            {scheduleType === SCHEDULED_TASK_TYPE_RECURRING && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    label="Frequency"
                  >
                    <MenuItem value={SCHEDULED_TASK_FREQUENCY_DAILY}>Daily</MenuItem>
                    <MenuItem value={SCHEDULED_TASK_FREQUENCY_WEEKLY}>Weekly</MenuItem>
                    <MenuItem value={SCHEDULED_TASK_FREQUENCY_MONTHLY}>Monthly</MenuItem>
                  </Select>
                </FormControl>

                <TimePicker
                  label="Time"
                  value={recurringTime}
                  onChange={(time) => setRecurringTime(time as Moment | null)}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />

                {/* Days of Week (for Weekly) */}
                {frequency === SCHEDULED_TASK_FREQUENCY_WEEKLY && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Days of Week
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {DAYS_OF_WEEK.map((day) => (
                        <FormControlLabel
                          key={day.value}
                          control={
                            <Checkbox
                              checked={daysOfWeek.includes(day.value)}
                              onChange={() => handleDayOfWeekToggle(day.value)}
                              size="small"
                            />
                          }
                          label={day.label.substring(0, 3)}
                          sx={{ mr: 0 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Day of Month (for Monthly) */}
                {frequency === SCHEDULED_TASK_FREQUENCY_MONTHLY && (
                  <FormControl fullWidth>
                    <InputLabel>Day of Month</InputLabel>
                    <Select
                      value={dayOfMonth}
                      onChange={(e) => setDayOfMonth(Number(e.target.value))}
                      label="Day of Month"
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}

            {/* Timezone */}
            <Autocomplete
              value={timezone}
              onChange={(_, value) => value && setTimezone(value)}
              options={COMMON_TIMEZONES}
              freeSolo
              renderInput={(params) => (
                <TextField {...params} label="Timezone" />
              )}
            />

            {/* Schedule Summary */}
            <Box
              sx={{
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Schedule: <strong>{getScheduleSummary()}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Timezone: {timezone}
              </Typography>
            </Box>
          </Box>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={saving}
          disabled={!name.trim() || !employeeId || !prompt.trim()}
        >
          {editingTask ? "Save Changes" : "Create Task"}
        </LoadingButton>
      </DialogActions>

      {/* Settings Dialog */}
      {settingsDialogOpen && (
        <ChatSettingsDialog
          open={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
          contextSettings={contextSettings || {}}
          setContextSettings={(settings: any) => {
            setContextSettings(settings);
          }}
          strategyOptions={[]}
          saveCompanyDefaultSettings={() => {}}
          saveSettings={() => {
            setSettingsDialogOpen(false);
          }}
          currentPlan={currentPlan}
          company={company}
          getToken={getToken}
        />
      )}
    </Dialog>
  );
}
